import TimeConstants from 'mini-datetime-utils/lib/dateValues/TimeConstants';
import { differenceInMinutes, OwaDate } from 'owa-datetime';
import { format } from 'owa-strings';
import { getOwaMiniCommonStrings } from 'owa-strings/owa-mini-common-strings';

let strings = null;

/**
 * Returns the event duration to be displayed
 * @param startDate start date of the event
 * @param endDate end date of the event
 * @param useShortFormatOnly Should always return in short notation even if there is only one unit to show. If true we return '2h' instead of '2 hours'
 */
export default function getEventDurationDisplayString(
    start: OwaDate,
    end: OwaDate,
    useShortFormatOnly: boolean = false
): string {
    return getEventDurationDisplayStringFromMinutes(
        differenceInMinutes(end, start),
        useShortFormatOnly
    );
}

/**
 * Returns the event duration to be displayed from the number of minutes passed
 * @param numberOfMinutes start date of the event
 * @param useShortFormatOnly Should always return in short notation even if there is only one unit to show. If true we return '2h' instead of '2 hours'
 */
export function getEventDurationDisplayStringFromMinutes(
    numberOfMinutes: number,
    useShortFormatOnly: boolean = false
): string {
    let eventDuration = '';
    const days = Math.floor(numberOfMinutes / TimeConstants.MinutesInOneDay);
    const hours = Math.floor(
        (numberOfMinutes % TimeConstants.MinutesInOneDay) / TimeConstants.MinutesInOneHour
    );
    const minutes = numberOfMinutes % TimeConstants.MinutesInOneHour;

    strings = getOwaMiniCommonStrings();
    if (days > 0) {
        eventDuration = getDurationStringWithDays(days, hours, minutes, useShortFormatOnly);
    } else if (hours > 0) {
        eventDuration = getDurationStringWithHours(hours, minutes, useShortFormatOnly);
    } else if (minutes >= 0) {
        eventDuration = getDurationStringWithMinutesOnly(minutes, useShortFormatOnly);
    }
    return eventDuration;
}

/**
 * Returns event duration string in days and may be hours and minutes if hours > 0 and minutes > 0 respectively
 */
function getDurationStringWithDays(
    days: number,
    hours: number,
    minutes: number,
    useShortFormatOnly: boolean
) {
    let eventDuration = '';
    if (hours > 0) {
        eventDuration = getDurationStringWithDaysHours(days, hours, minutes);
    } else if (minutes > 0) {
        eventDuration = format(strings.eventDurationDaysMinutesShort, days, minutes);
    } else {
        eventDuration = getDurationStringWithDaysOnly(days, useShortFormatOnly);
    }
    return eventDuration;
}

/**
 * Returns event duration string in days and hours and may be minutes if minutes > 0
 */
function getDurationStringWithDaysHours(days: number, hours: number, minutes: number) {
    let eventDuration = '';
    if (minutes > 0) {
        // 1d 2h 30m
        eventDuration = format(strings.eventDurationDaysHoursMinutesShort, days, hours, minutes);
    } else {
        // 1d 2h
        eventDuration = format(strings.eventDurationDaysHoursShort, days, hours);
    }
    return eventDuration;
}

/**
 * Returns event duration string in only days
 */
function getDurationStringWithDaysOnly(days: number, useShortFormatOnly: boolean) {
    let formatString = '';
    if (useShortFormatOnly) {
        // 1d
        formatString = strings.eventDurationDaysShort;
    } else {
        // 1 day
        formatString = days > 1 ? strings.eventDurationMultiDay : strings.eventDurationSingleDay;
    }
    return format(formatString, days);
}

/**
 * Returns event duration string in hours and may be minutes if minutes > 0
 */
function getDurationStringWithHours(hours: number, minutes: number, useShortFormatOnly: boolean) {
    let eventDuration = '';
    if (minutes > 0) {
        // 2h 30m
        eventDuration = format(strings.eventDurationHoursMinutesShort, hours, minutes);
    } else {
        eventDuration = getDurationStringWithHoursOnly(hours, useShortFormatOnly);
    }
    return eventDuration;
}

/**
 * Returns event duration string in only hours
 */
function getDurationStringWithHoursOnly(hours: number, useShortFormatOnly: boolean) {
    let formatString = '';
    if (useShortFormatOnly) {
        // 2h
        formatString = strings.eventDurationHoursShort;
    } else {
        // 2 hours
        formatString = hours > 1 ? strings.eventDurationMultiHour : strings.eventDurationSingleHour;
    }
    return format(formatString, hours);
}

/**
 * Returns event duration string in only minutes
 */
function getDurationStringWithMinutesOnly(minutes: number, useShortFormatOnly: boolean) {
    let formatString = '';
    if (useShortFormatOnly) {
        // 30m
        formatString = strings.eventDurationMinutesShort;
    } else {
        // 30 minutes
        formatString =
            minutes !== 1 ? strings.eventDurationMultiMinute : strings.eventDurationSingleMinute;
    }
    return format(formatString, minutes);
}
